import React, { useEffect, useRef } from 'react'

import { ArrowLeft, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Pagination } from '@/core/api/dtos/common/metaPagination'
import { Schedule } from '@/core/api/entities/schedule'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Input } from '@/shared/components/atoms/Input'
import { PageLayout } from '@/shared/components/organisms/Layout/PageLayout/PageLayout'

import { AssignScheduleModal } from './components/AssignScheduleModal'
import { DuplicateScheduleModal } from './components/DuplicateScheduleModal'
import { RemoveScheduleModal } from './components/RemoveScheduleModal'
import { ScheduleCard } from './components/ScheduleCard'
import ScheduleSkeleton from './components/ScheduleSkeleton'

// Types
interface ViewAllSchedulesPageUIProps {
	title: string
	searchQuery: string
	onSearchChange: (query: string) => void
	schedules: Schedule[]
	isLoading: boolean
	showSearchResults: boolean
	onScheduleClick: (schedule: Schedule) => void
	onFavoriteClick?: (schedule: Schedule, isFavoritesSection: boolean) => void
	hasMore: boolean
	isLoadingMore: boolean
	onLoadMore: () => void
	scheduleType: 'assigned' | 'favorites' | 'all' | 'in-use'
	pagination?: Pagination
	currentPage: number
	isEditable: boolean
	onAssign: (scheduleId: number, name: string) => void
	onDuplicate?: (scheduleId: number, name: string, description: string) => void
	onEdit?: (scheduleId: number) => void
	onRemove?: (scheduleId: number, name: string, description: string) => void
	// Modal props
	isAssignModalOpen: boolean
	onAssignModalChange: (open: boolean) => void
	selectedScheduleForAssign: { id: string; name: string } | null
	isRemoveModalOpen: boolean
	onRemoveModalChange: (open: boolean) => void
	selectedScheduleForRemove: {
		id: string
		name: string
		description?: string
	} | null
	isDuplicateModalOpen: boolean
	onDuplicateModalChange: (open: boolean) => void
	selectedScheduleForDuplicate: {
		id: string
		name: string
		description?: string
	} | null
}

// Main Component
const ViewAllSchedulesPageUI: React.FC<ViewAllSchedulesPageUIProps> = ({
	title,
	searchQuery,
	onSearchChange,
	schedules,
	isLoading,
	showSearchResults,
	onScheduleClick,
	hasMore,
	isLoadingMore,
	onLoadMore,
	pagination,
	currentPage,
	isEditable,
	onAssign,
	onDuplicate,
	onEdit,
	onRemove,
	// Modal props
	isAssignModalOpen,
	onAssignModalChange,
	selectedScheduleForAssign,
	isRemoveModalOpen,
	onRemoveModalChange,
	selectedScheduleForRemove,
	isDuplicateModalOpen,
	onDuplicateModalChange,
	selectedScheduleForDuplicate,
}) => {
	const { t } = useTranslation('common')
	const observerTarget = useRef<HTMLDivElement>(null)
	const navigate = useNavigate()

	// Infinite scroll observer
	useEffect(() => {
		if (showSearchResults) {
			return // Don't use infinite scroll for search
		}

		// Calculate hasMore from pagination
		const calculatedHasMore = pagination
			? currentPage < pagination.total_pages
			: false

		if (!calculatedHasMore) {
			return // Don't observe if no more pages
		}

		let observer: IntersectionObserver | null = null
		let currentTarget: HTMLDivElement | null = null

		const timeoutId = setTimeout(() => {
			observer = new IntersectionObserver(
				(entries) => {
					const entry = entries[0]

					if (
						entry.isIntersecting &&
						calculatedHasMore &&
						!isLoadingMore &&
						!isLoading
					) {
						onLoadMore()
					}
				},
				{
					threshold: 0,
					rootMargin: '200px', // Trigger 200px before reaching the bottom
				}
			)

			currentTarget = observerTarget.current
			if (currentTarget) {
				observer.observe(currentTarget)
			}
		}, 100)

		return () => {
			clearTimeout(timeoutId)
			if (observer && currentTarget) {
				observer.unobserve(currentTarget)
			}
		}
	}, [
		hasMore,
		isLoadingMore,
		isLoading,
		onLoadMore,
		showSearchResults,
		pagination,
		currentPage,
	])

	return (
		<>
			<PageLayout
				showTopAppBar={false}
				customHeader={
					<div className="flex items-center justify-between px-3 py-5 md:px-0 md:py-0">
						<div className="flex items-center gap-4">
							<button
								onClick={() => navigate('/schedules')}
								className="text-foreground hover:text-muted-foreground transition-colors"
							>
								<ArrowLeft className="h-5 w-5 md:h-6 md:w-6" />
							</button>
							<h2 className="text-lg font-normal tracking-tight md:text-3xl truncate">
								{title}
							</h2>
						</div>
					</div>
				}
			>
				<div className="space-y-6">
					{/* Search */}
					<div className="flex w-full items-center gap-4 justify-between">
						<div className="relative flex flex-1 md:max-w-[400px]">
							<Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" />
							<Input
								type="search"
								placeholder={t('actions.search')}
								value={searchQuery}
								onChange={(e) => onSearchChange(e.target.value)}
								className="pl-10 w-full"
							/>
						</div>
					</div>

					{/* Schedule Grid */}
					{isLoading && schedules.length === 0 ? (
						<div className="grid grid-cols-2 gap-4 mb-8 sm:grid-cols-[repeat(auto-fill,200px)] items-start">
							{Array.from({ length: 25 }).map((_, index) => (
								<ScheduleSkeleton key={index} count={1} />
							))}
						</div>
					) : schedules.length > 0 ? (
						<>
							<div className="grid grid-cols-2 gap-4 mb-8 sm:grid-cols-[repeat(auto-fill,200px)] items-start">
								{schedules.map((schedule) => (
									<ScheduleCard
										key={schedule.id}
										schedule={schedule}
										className="w-full"
										showActions={isEditable || !schedule.public}
										onClick={onScheduleClick}
										onAssign={isEditable ? onAssign : undefined}
										onDuplicate={isEditable ? onDuplicate : undefined}
										onEdit={isEditable ? onEdit : undefined}
										onRemove={isEditable ? onRemove : undefined}
									/>
								))}
							</div>

							{/* Infinite scroll trigger */}
							{!showSearchResults &&
							pagination &&
							currentPage < pagination.total_pages ? (
								<div
									ref={observerTarget}
									className="min-h-[200px] mt-6 w-full flex flex-col"
								>
									<div className="min-h-[100px] w-full" />
								</div>
							) : null}

							{/* Floating loading indicator */}
							{isLoadingMore && (
								<div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[1000] bg-card border border-border rounded-lg px-4 py-2 shadow-lg flex items-center justify-center backdrop-blur-sm animate-[slideUp_0.3s_ease-out]">
									<div className="flex items-center gap-2">
										<div className="w-4 h-4 border-2 border-foreground/30 border-t-foreground rounded-full animate-spin" />
										<span className="text-foreground text-sm font-medium">
											{t('actions.loading')}
										</span>
									</div>
								</div>
							)}
						</>
					) : (
						<div className="flex items-center justify-center py-16 px-8">
							<p className="text-muted-foreground text-base m-0">
								{t('entities.schedule.no_schedules_found')}
							</p>
						</div>
					)}
				</div>
			</PageLayout>

			{/* Assign Schedule Modal */}
			<AssignScheduleModal
				open={isAssignModalOpen}
				onOpenChange={onAssignModalChange}
				scheduleId={selectedScheduleForAssign?.id}
				scheduleName={selectedScheduleForAssign?.name}
			/>

			{/* Remove Schedule Modal */}
			<RemoveScheduleModal
				open={isRemoveModalOpen}
				onOpenChange={onRemoveModalChange}
				scheduleId={selectedScheduleForRemove?.id}
				scheduleName={selectedScheduleForRemove?.name}
				scheduleDescription={selectedScheduleForRemove?.description}
			/>

			{/* Duplicate Schedule Modal */}
			<DuplicateScheduleModal
				open={isDuplicateModalOpen}
				onOpenChange={onDuplicateModalChange}
				scheduleId={selectedScheduleForDuplicate?.id}
				scheduleName={selectedScheduleForDuplicate?.name}
				scheduleDescription={selectedScheduleForDuplicate?.description}
			/>
		</>
	)
}

export default ViewAllSchedulesPageUI
